import { TENANT_IDENTITY_COLOUR_POLICY, type HexColour } from '../design-tokens/source';
import { assessTenantIdentityColour, type TenantIdentityColourAssessment } from './colour-policy';

export const TENANT_BRAND_PROJECTION_VERSION = '1.0.0-candidate.1' as const;
export const TENANT_LEGAL_IDENTITY_PROJECTION_VERSION = '1.0.0-candidate.1' as const;
export const TENANT_BRAND_PERSISTENCE_CONTRACT_VERSION = TENANT_IDENTITY_COLOUR_POLICY.version;

export const TENANT_BRAND_WRITABLE_KEYS = ['logoUrl', 'primaryColour'] as const;
export type TenantBrandWritableKey = (typeof TENANT_BRAND_WRITABLE_KEYS)[number];

export const LEGACY_BRAND_KEYS_REJECTED_BY_WRITER = [
  'primaryColor',
  'secondaryColor',
  'secondaryColour',
  'accentColor',
  'accentColour',
  'fontFamily',
] as const;

export interface TenantBrandProjection {
  readonly version: typeof TENANT_BRAND_PROJECTION_VERSION;
  readonly logoUrl: string | null;
  readonly logoSource: 'tenants.logo_url' | 'brand_config.logoUrl' | 'canonical_fallback';
  readonly primaryColour: HexColour | null;
  readonly colourAssessment: TenantIdentityColourAssessment;
  readonly diagnostics: readonly TenantBrandProjectionDiagnostic[];
}

export interface TenantBrandProjectionDiagnostic {
  readonly code:
    | 'ignored_non_brand_key'
    | 'legacy_brand_key_rejected'
    | 'legacy_primary_colour_pending_migration'
    | 'legacy_primary_colour_conflict'
    | 'invalid_logo_url'
    | 'invalid_or_fallback_colour';
  readonly key: string;
}

export interface TenantBrandProjectionSource {
  /** Mixed legacy JSON bag. Only the two allowlisted identity keys are projected. */
  readonly brandConfig: unknown;
  /** The current top-level tenant logo authority, preferred over the legacy bag. */
  readonly logoUrl: unknown;
}

export interface TenantLegalIdentitySource {
  readonly displayName: unknown;
  readonly legalName: unknown;
  readonly abn: unknown;
  readonly senderOfRecordName: unknown;
  readonly senderOfRecordEmail: unknown;
}

export interface TenantLegalIdentityProjection {
  readonly version: typeof TENANT_LEGAL_IDENTITY_PROJECTION_VERSION;
  readonly displayName: string;
  readonly legalName: string;
  readonly legalNameSource: 'legal_name' | 'display_name_fallback';
  readonly abn: string | null;
  readonly senderOfRecord: {
    readonly name: string;
    readonly email: string | null;
  };
}

export type TenantLegalIdentityProjectionResult =
  | { readonly ok: true; readonly value: TenantLegalIdentityProjection }
  | {
      readonly ok: false;
      readonly reason:
        | 'missing_display_name'
        | 'missing_sender_of_record'
        | 'invalid_abn'
        | 'invalid_sender_email';
    };

export type TenantBrandPatchValidation =
  | {
      readonly ok: true;
      readonly value: {
        readonly logoUrl?: string | null;
        readonly primaryColour?: HexColour | null;
      };
    }
  | {
      readonly ok: false;
      readonly reason:
        | 'patch_must_be_object'
        | 'patch_must_not_be_empty'
        | 'unsupported_key'
        | 'invalid_logo_url'
        | 'invalid_primary_colour';
      readonly key?: string;
      readonly colourStatus?: TenantIdentityColourAssessment['status'];
    };

function recordFromUnknown(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function normaliseTenantLogoUrl(value: unknown): string | null {
  const input = nonEmptyString(value);
  if (!input || input.length > 2048) return null;

  try {
    const parsed = new URL(input);
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function projectTenantBrand(source: TenantBrandProjectionSource): TenantBrandProjection {
  const config = recordFromUnknown(source.brandConfig);
  const diagnostics: TenantBrandProjectionDiagnostic[] = [];

  for (const key of Object.keys(config).sort()) {
    if ((LEGACY_BRAND_KEYS_REJECTED_BY_WRITER as readonly string[]).includes(key)) {
      diagnostics.push({ code: 'legacy_brand_key_rejected', key });
    } else if (!(TENANT_BRAND_WRITABLE_KEYS as readonly string[]).includes(key)) {
      diagnostics.push({ code: 'ignored_non_brand_key', key });
    }
  }
  if (config.primaryColor !== undefined) {
    diagnostics.push({
      code:
        config.primaryColour === undefined
          ? 'legacy_primary_colour_pending_migration'
          : 'legacy_primary_colour_conflict',
      key: 'brand_config.primaryColor',
    });
  }

  const topLevelLogo = normaliseTenantLogoUrl(source.logoUrl);
  const legacyLogo = normaliseTenantLogoUrl(config.logoUrl);
  if (source.logoUrl != null && !topLevelLogo) {
    diagnostics.push({ code: 'invalid_logo_url', key: 'tenants.logo_url' });
  }
  if (config.logoUrl != null && !legacyLogo) {
    diagnostics.push({ code: 'invalid_logo_url', key: 'brand_config.logoUrl' });
  }

  const colourAssessment = assessTenantIdentityColour(config.primaryColour);
  if (colourAssessment.status !== 'accepted') {
    diagnostics.push({ code: 'invalid_or_fallback_colour', key: 'brand_config.primaryColour' });
  }

  return {
    version: TENANT_BRAND_PROJECTION_VERSION,
    logoUrl: topLevelLogo ?? legacyLogo,
    logoSource: topLevelLogo
      ? 'tenants.logo_url'
      : legacyLogo
        ? 'brand_config.logoUrl'
        : 'canonical_fallback',
    primaryColour: colourAssessment.status === 'accepted' ? colourAssessment.normalised : null,
    colourAssessment,
    diagnostics,
  };
}

export function validateTenantBrandPatch(patch: unknown): TenantBrandPatchValidation {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
    return { ok: false, reason: 'patch_must_be_object' };
  }

  const record = patch as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length === 0) return { ok: false, reason: 'patch_must_not_be_empty' };

  const unsupportedKey = keys.find(
    (key) => !(TENANT_BRAND_WRITABLE_KEYS as readonly string[]).includes(key),
  );
  if (unsupportedKey) return { ok: false, reason: 'unsupported_key', key: unsupportedKey };

  const value: { logoUrl?: string | null; primaryColour?: HexColour | null } = {};
  if ('logoUrl' in record) {
    if (record.logoUrl === null) {
      value.logoUrl = null;
    } else {
      const logoUrl = normaliseTenantLogoUrl(record.logoUrl);
      if (!logoUrl) return { ok: false, reason: 'invalid_logo_url', key: 'logoUrl' };
      value.logoUrl = logoUrl;
    }
  }

  if ('primaryColour' in record) {
    if (record.primaryColour === null) {
      value.primaryColour = null;
    } else {
      const assessment = assessTenantIdentityColour(record.primaryColour);
      if (assessment.status !== 'accepted' || !assessment.normalised) {
        return {
          ok: false,
          reason: 'invalid_primary_colour',
          key: 'primaryColour',
          colourStatus: assessment.status,
        };
      }
      value.primaryColour = assessment.normalised;
    }
  }

  return { ok: true, value };
}

function normaliseAbn(value: unknown): string | null | 'invalid' {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string') return 'invalid';
  const digits = value.replace(/\s/gu, '');
  return /^\d{11}$/u.test(digits)
    ? `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`
    : 'invalid';
}

function normaliseEmail(value: unknown): string | null | 'invalid' {
  if (value === null || value === undefined || value === '') return null;
  const email = nonEmptyString(value)?.toLowerCase();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) return 'invalid';
  return email;
}

export function projectTenantLegalIdentity(
  source: TenantLegalIdentitySource,
): TenantLegalIdentityProjectionResult {
  const displayName = nonEmptyString(source.displayName);
  if (!displayName) return { ok: false, reason: 'missing_display_name' };

  const explicitLegalName = nonEmptyString(source.legalName);
  const legalName = explicitLegalName ?? displayName;

  const senderName = nonEmptyString(source.senderOfRecordName);
  if (!senderName) return { ok: false, reason: 'missing_sender_of_record' };

  const abn = normaliseAbn(source.abn);
  if (abn === 'invalid') return { ok: false, reason: 'invalid_abn' };

  const senderEmail = normaliseEmail(source.senderOfRecordEmail);
  if (senderEmail === 'invalid') return { ok: false, reason: 'invalid_sender_email' };

  return {
    ok: true,
    value: {
      version: TENANT_LEGAL_IDENTITY_PROJECTION_VERSION,
      displayName,
      legalName,
      legalNameSource: explicitLegalName ? 'legal_name' : 'display_name_fallback',
      abn,
      senderOfRecord: { name: senderName, email: senderEmail },
    },
  };
}
